'use client';

import { useState } from 'react';

interface EngineGuideProps {
  id: string;
  frameW: number;
  frameH: number;
  totalFrames: number;
  pngName: string;
}

export default function EngineGuide({
  id,
  frameW,
  frameH,
  totalFrames,
  pngName,
}: EngineGuideProps) {
  const [activeEngine, setActiveEngine] = useState<'unity' | 'godot' | 'gamemaker' | 'phaser'>('unity');

  const cols = Math.round(Math.sqrt(totalFrames)) || 4;

  return (
    <div style={{
      background: 'var(--surface-card)',
      border: '1px solid var(--hairline-border)',
      borderRadius: 'var(--radius-lg)',
      padding: '24px',
      boxShadow: 'var(--shadow-card)',
      display: 'flex',
      flexDirection: 'column',
    }}>
      <h2 style={{ fontSize: '20px', fontWeight: 700, color: '#fff' }}>
        Drop it in
      </h2>

      {/* Engine Tabs */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginTop: '16px' }}>
        {(['unity', 'godot', 'gamemaker', 'phaser'] as const).map((eng) => {
          const isActive = eng === activeEngine;
          const label = eng === 'gamemaker' ? 'GameMaker' : eng.charAt(0).toUpperCase() + eng.slice(1);
          return (
            <button
              key={eng}
              onClick={() => setActiveEngine(eng)}
              style={{
                background: isActive ? 'var(--primary)' : 'var(--surface-elevated)',
                color: isActive ? 'var(--on-primary)' : 'var(--muted-strong)',
                border: isActive ? '1px solid var(--primary)' : '1px solid var(--hairline-border)',
                fontSize: '13px',
                fontWeight: 600,
                padding: '6px 16px',
                borderRadius: '999px',
                cursor: 'pointer',
                transition: 'all 0.15s ease',
              }}
            >
              {label}
            </button>
          );
        })}
      </div>

      {/* Steps List */}
      <ol style={{ listStyle: 'none', marginTop: '18px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
        {activeEngine === 'unity' && (
          <>
            <li style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', fontSize: '13px', color: 'var(--body-text)', lineHeight: 1.5 }}>
              <span style={{ width: '22px', height: '22px', borderRadius: '50%', background: 'rgba(14, 203, 129, 0.15)', color: 'var(--primary)', fontSize: '11px', fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: '1px' }}>1</span>
              <span>Drag the PNG into Assets, then select it in the Inspector.</span>
            </li>
            <li style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', fontSize: '13px', color: 'var(--body-text)', lineHeight: 1.5 }}>
              <span style={{ width: '22px', height: '22px', borderRadius: '50%', background: 'rgba(14, 203, 129, 0.15)', color: 'var(--primary)', fontSize: '11px', fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: '1px' }}>2</span>
              <span>Sprite Mode → Multiple. Open Sprite Editor.</span>
            </li>
            <li style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', fontSize: '13px', color: 'var(--body-text)', lineHeight: 1.5 }}>
              <span style={{ width: '22px', height: '22px', borderRadius: '50%', background: 'rgba(14, 203, 129, 0.15)', color: 'var(--primary)', fontSize: '11px', fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: '1px' }}>3</span>
              <span>Slice → Grid By Cell Size → X {frameW}, Y {frameH} → Slice → Apply.</span>
            </li>
            <li style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', fontSize: '13px', color: 'var(--body-text)', lineHeight: 1.5 }}>
              <span style={{ width: '22px', height: '22px', borderRadius: '50%', background: 'rgba(14, 203, 129, 0.15)', color: 'var(--primary)', fontSize: '11px', fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: '1px' }}>4</span>
              <span>Select all {totalFrames} sliced frames and drag them into the scene to create an Animation clip.</span>
            </li>
          </>
        )}

        {activeEngine === 'godot' && (
          <>
            <li style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', fontSize: '13px', color: 'var(--body-text)', lineHeight: 1.5 }}>
              <span style={{ width: '22px', height: '22px', borderRadius: '50%', background: 'rgba(14, 203, 129, 0.15)', color: 'var(--primary)', fontSize: '11px', fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: '1px' }}>1</span>
              <span>Kéo thả file <code>{pngName}</code> vào FileSystem của Godot 4.</span>
            </li>
            <li style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', fontSize: '13px', color: 'var(--body-text)', lineHeight: 1.5 }}>
              <span style={{ width: '22px', height: '22px', borderRadius: '50%', background: 'rgba(14, 203, 129, 0.15)', color: 'var(--primary)', fontSize: '11px', fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: '1px' }}>2</span>
              <span>Tạo node <strong>AnimatedSprite2D</strong> trong Scene.</span>
            </li>
            <li style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', fontSize: '13px', color: 'var(--body-text)', lineHeight: 1.5 }}>
              <span style={{ width: '22px', height: '22px', borderRadius: '50%', background: 'rgba(14, 203, 129, 0.15)', color: 'var(--primary)', fontSize: '11px', fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: '1px' }}>3</span>
              <span>Trong Inspector, tạo <strong>SpriteFrames</strong> mới, bấm Add Frames from Sprite Sheet.</span>
            </li>
            <li style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', fontSize: '13px', color: 'var(--body-text)', lineHeight: 1.5 }}>
              <span style={{ width: '22px', height: '22px', borderRadius: '50%', background: 'rgba(14, 203, 129, 0.15)', color: 'var(--primary)', fontSize: '11px', fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: '1px' }}>4</span>
              <span>Chia lưới tự động ({cols} cột) và chọn tất cả {totalFrames} frames. Bấm Autoplay on Load.</span>
            </li>
          </>
        )}

        {activeEngine === 'gamemaker' && (
          <>
            <li style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', fontSize: '13px', color: 'var(--body-text)', lineHeight: 1.5 }}>
              <span style={{ width: '22px', height: '22px', borderRadius: '50%', background: 'rgba(14, 203, 129, 0.15)', color: 'var(--primary)', fontSize: '11px', fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: '1px' }}>1</span>
              <span>Tạo một <strong>Sprite</strong> mới trong Asset Browser.</span>
            </li>
            <li style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', fontSize: '13px', color: 'var(--body-text)', lineHeight: 1.5 }}>
              <span style={{ width: '22px', height: '22px', borderRadius: '50%', background: 'rgba(14, 203, 129, 0.15)', color: 'var(--primary)', fontSize: '11px', fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: '1px' }}>2</span>
              <span>Bấm <strong>Edit Image</strong>, chọn <strong>Image → Import Strip Image</strong>.</span>
            </li>
            <li style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', fontSize: '13px', color: 'var(--body-text)', lineHeight: 1.5 }}>
              <span style={{ width: '22px', height: '22px', borderRadius: '50%', background: 'rgba(14, 203, 129, 0.15)', color: 'var(--primary)', fontSize: '11px', fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: '1px' }}>3</span>
              <span>Đặt <strong>Number of Frames: {totalFrames}</strong>, Frame Size: <strong>{frameW}×{frameH}</strong>.</span>
            </li>
            <li style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', fontSize: '13px', color: 'var(--body-text)', lineHeight: 1.5 }}>
              <span style={{ width: '22px', height: '22px', borderRadius: '50%', background: 'rgba(14, 203, 129, 0.15)', color: 'var(--primary)', fontSize: '11px', fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: '1px' }}>4</span>
              <span>Bấm <strong>Convert</strong>. Sprite đã sẵn sàng chạy trong GameMaker!</span>
            </li>
          </>
        )}

        {activeEngine === 'phaser' && (
          <>
            <li style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', fontSize: '13px', color: 'var(--body-text)', lineHeight: 1.5 }}>
              <span style={{ width: '22px', height: '22px', borderRadius: '50%', background: 'rgba(14, 203, 129, 0.15)', color: 'var(--primary)', fontSize: '11px', fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: '1px' }}>1</span>
              <span>Dùng <code>this.load.spritesheet(&apos;{id}&apos;, &apos;assets/{pngName}&apos;, &#123; frameWidth: {frameW}, frameHeight: {frameH} &#125;)</code> trong <code>preload()</code>.</span>
            </li>
            <li style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', fontSize: '13px', color: 'var(--body-text)', lineHeight: 1.5 }}>
              <span style={{ width: '22px', height: '22px', borderRadius: '50%', background: 'rgba(14, 203, 129, 0.15)', color: 'var(--primary)', fontSize: '11px', fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: '1px' }}>2</span>
              <span>Trong <code>create()</code>, khởi tạo animation bằng <code>this.anims.create(&#123; key: &apos;{id}_anim&apos;, frames: this.anims.generateFrameNumbers(...) &#125;)</code>.</span>
            </li>
            <li style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', fontSize: '13px', color: 'var(--body-text)', lineHeight: 1.5 }}>
              <span style={{ width: '22px', height: '22px', borderRadius: '50%', background: 'rgba(14, 203, 129, 0.15)', color: 'var(--primary)', fontSize: '11px', fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: '1px' }}>3</span>
              <span>Gán sprite: <code>const s = this.add.sprite(x, y, &apos;{id}&apos;)</code>.</span>
            </li>
            <li style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', fontSize: '13px', color: 'var(--body-text)', lineHeight: 1.5 }}>
              <span style={{ width: '22px', height: '22px', borderRadius: '50%', background: 'rgba(14, 203, 129, 0.15)', color: 'var(--primary)', fontSize: '11px', fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: '1px' }}>4</span>
              <span>Gọi <code>s.play(&apos;{id}_anim&apos;)</code> để chạy chuyển động liên tục.</span>
            </li>
          </>
        )}
      </ol>

      <a
        href={`/docs/integration-${activeEngine}`}
        style={{
          color: 'var(--primary)',
          fontSize: '13px',
          fontWeight: 600,
          marginTop: '20px',
          display: 'inline-block',
        }}
      >
        Full {activeEngine.charAt(0).toUpperCase() + activeEngine.slice(1)} guide →
      </a>
    </div>
  );
}
